// src/utils/dateUtils.js
// Centralized date utilities to handle timezone issues consistently

/**
 * Formats a date string for display (from API to UI)
 * Uses the safe approach with explicit UTC time to avoid timezone shifts
 */
export function formatDateForDisplay(dateString) {
  if (!dateString) return '';
  
  try {
    let date;
    
    // Check if it's already a full datetime string
    if (dateString.includes('T') || dateString.includes(' ')) {
      date = new Date(dateString);
    } else {
      // Use the safe approach - append T12:00:00Z to treat as UTC noon
      // This prevents timezone-related date shifts
      date = new Date(dateString + 'T12:00:00Z');
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Fecha inválida';
    }
    
    // Format in Spanish format (DD/MM/YYYY)
    return date.toLocaleDateString('es-ES');
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return 'Fecha inválida';
  }
}

/**
 * Formats a date for API submission (from UI to API)
 * Ensures consistent YYYY-MM-DD format without timezone issues
 */
export function formatDateForAPI(dateInput) {
  if (!dateInput) return '';
  
  try {
    // If it's already in YYYY-MM-DD format, return as-is
    if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateInput;
    }
    
    // Create date object
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input for API:', dateInput);
      return '';
    }
    
    // Use local methods for consistent formatting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for API:', dateInput, error);
    return '';
  }
}

/**
 * Formats a date for HTML input[type="date"] 
 * Returns YYYY-MM-DD format
 */
export function formatDateForInput(dateInput) {
  if (!dateInput) return '';
  
  try {
    let date;
    
    // Handle different input types
    if (typeof dateInput === 'string') {
      // If it's already in YYYY-MM-DD format, return as-is
      if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateInput;
      }
      
      // For date strings from API, handle carefully
      if (!dateInput.includes('T') && !dateInput.includes(' ')) {
        date = new Date(dateInput + 'T12:00:00Z');
      } else {
        date = new Date(dateInput);
      }
    } else {
      date = new Date(dateInput);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return '';
    }
    
    // Format for input field
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', dateInput, error);
    return '';
  }
}

/**
 * Calculate age from birth date string
 * Uses UTC to avoid timezone-related calculation errors
 */
export function calculateAge(birthDateString) {
  if (!birthDateString) return '';
  
  try {
    let birthDate;
    
    // Handle different date formats
    if (birthDateString.includes('T') || birthDateString.includes(' ')) {
      birthDate = new Date(birthDateString);
    } else {
      // Parse birth date as UTC noon to avoid timezone shifts
      birthDate = new Date(birthDateString + 'T12:00:00Z');
    }
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
      console.warn('Invalid birth date:', birthDateString);
      return '';
    }
    
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    console.error('Error calculating age:', birthDateString, error);
    return '';
  }
}

/**
 * Format time string (HH:MM:SS to HH:MM)
 */
export function formatTime(timeString) {
  if (!timeString) return '';
  try {
    return timeString.substring(0, 5);
  } catch (error) {
    console.error('Error formatting time:', timeString, error);
    return '';
  }
}

/**
 * Get today's date in YYYY-MM-DD format for form inputs
 * Uses local date but formats consistently
 */
export function getTodayForInput() {
  try {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error getting today for input:', error);
    return '';
  }
}